
import React, { useState, useEffect } from 'react';

// Props Interface
// Props Interface
interface ScheduleNodeConfigProps {
    data: any;
    onUpdate: (key: string, value: any) => void;
}

export const ScheduleNodeConfig: React.FC<ScheduleNodeConfigProps> = ({ data, onUpdate }) => {
    // Mode: 'simple' or 'advanced' (Default to simple unless complex cron exists)
    const [mode, setMode] = useState<'simple' | 'advanced'>('simple');

    // Simple State
    const [frequency, setFrequency] = useState<string>('day');
    const [time, setTime] = useState<string>('09:00'); // HH:mm
    const [weekDay, setWeekDay] = useState<string>('1'); // 1=Mon

    // Advanced State
    const [cronExpression, setCronExpression] = useState<string>(data.cron || '');

    // Initialize from data
    useEffect(() => {
        if (data.cron) {
            setCronExpression(data.cron);
            // Try to reverse-engineer simple state (basic heuristic)
            if (data.cron === '*/15 * * * *') { setFrequency('minute'); setTime('00:15'); }
            else if (data.cron === '0 * * * *') { setFrequency('hour'); setTime('00:00'); }
            else if (data.cron === '0 0 * * *') { setFrequency('day'); setTime('00:00'); }
            else if (/^(\d+)\s+(\d+)\s+\*\s+\*\s+\*$/.test(data.cron)) {
                const parts = data.cron.split(' ');
                setFrequency('day');
                setTime(`${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`);
            }
            else {
                // Too complex to reverse perfectly, stick to advanced if unknown pattern
                if (!data.mode) setMode('advanced');
            }
        }
        if (data.mode) setMode(data.mode);
    }, []);

    // Helper: Update Parent
    const updateCron = (newCron: string, newMode: 'simple' | 'advanced') => {
        setCronExpression(newCron);
        onUpdate('cron', newCron);
        // Defer the second property update to the next tick to prevent React state payload overwrite
        // if the parent's onUpdate handler doesn't strictly use functional updates.
        setTimeout(() => {
            onUpdate('mode', newMode);
        }, 10);
    };

    // Generate Cron from Simple Inputs
    useEffect(() => {
        if (mode === 'simple') {
            let newCron = '';
            const [hh, mm] = time.split(':');
            const h = parseInt(hh) || 0;
            const m = parseInt(mm) || 0;

            if (frequency === 'minute') {
                newCron = '*/15 * * * *'; // Every 15 mins
            }
            else if (frequency === 'hour') {
                newCron = '0 * * * *'; // Every hour at :00
            }
            else if (frequency === 'day') {
                newCron = `${m} ${h} * * *`;
            }
            else if (frequency === 'week') {
                newCron = `${m} ${h} * * ${weekDay}`;
            }

            if (newCron !== cronExpression) {
                updateCron(newCron, 'simple');
            }
        }
    }, [frequency, time, weekDay, mode]);

    const handleModeToggle = () => {
        const newMode = mode === 'simple' ? 'advanced' : 'simple';
        setMode(newMode);
        // If switching to simple, it will auto-regenerate based on state.
        // If switching to advanced, it keeps current cron.
    };

    return (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                    ⏱️ Schedule Trigger
                </h3>
                <button
                    onClick={handleModeToggle}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                    {mode === 'simple' ? 'Switch to Advanced' : 'Switch to Simple'}
                </button>
            </div>

            {mode === 'simple' ? (
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Run Frequency</label>
                        <select
                            value={frequency}
                            onChange={(e) => setFrequency(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="hour">Every Hour</option>
                            <option value="day">Daily</option>
                            <option value="week">Weekly</option>
                        </select>
                    </div>

                    {frequency !== 'hour' && (
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Time (Local)</label>
                            <input
                                type="time"
                                value={time}
                                onChange={(e) => setTime(e.target.value)}
                                className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                            />
                        </div>
                    )}

                    {frequency === 'week' && (
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">On Day</label>
                            <select
                                value={weekDay}
                                onChange={(e) => setWeekDay(e.target.value)}
                                className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                            >
                                <option value="1">Monday</option>
                                <option value="2">Tuesday</option>
                                <option value="3">Wednesday</option>
                                <option value="4">Thursday</option>
                                <option value="5">Friday</option>
                                <option value="6">Saturday</option>
                                <option value="0">Sunday</option>
                            </select>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Cron Expression</label>
                        <input
                            type="text"
                            value={cronExpression}
                            onChange={(e) => updateCron(e.target.value, 'advanced')}
                            placeholder="0 9 * * *"
                            className="w-full font-mono text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-400 mt-1">Format: sec min hour day month dow</p>
                    </div>
                </div>
            )}

            <div className="pt-2 border-t border-gray-200">
                <div className="bg-white p-2 rounded border border-gray-100">
                    <span className="text-xs font-mono text-gray-500 block mb-1">Generated Config:</span>
                    <code className="text-sm font-bold text-indigo-600 block">{cronExpression || 'Not Scheduled'}</code>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 italic">
                    Note: Schedule is activated when Flow is Saved and Active.
                </p>
            </div>
        </div>
    );
};
